import { IDENTITY_MATRIX, invertMatrix, multiplyMatrix, transformPoint, transformRectBoundsPrecise, type Matrix, type Point, type Rect } from './geometry';
import { EMPTY_SELECTION, type SelectionState } from './selection';
import { RasterTileStore } from './tiles';
import type { EditableText, VectorScene } from '../vector/types';
import { textLayoutBounds } from '../vector/text-layout';
import { DEFAULT_BLACK_WHITE, type BlackWhiteSettings, type GradientMapSettings, type LevelsSettings, type ToneCurves } from '../filters/tone';

export type DocumentId = string;
export type LayerId = string;
export type MaskId = string;

export interface Guide {
  id: string;
  axis: 'horizontal' | 'vertical';
  position: number;
  color?: string;
}

export type BlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'color-dodge'
  | 'color-burn'
  | 'hard-light'
  | 'soft-light'
  | 'difference'
  | 'exclusion';

export type LayerColor = 'none' | 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'violet' | 'gray';

export interface LayerMask {
  id: MaskId;
  enabled: boolean;
  linked: boolean;
  defaultAlpha: 0 | 255;
  tiles: RasterTileStore;
}

export interface LayerBase {
  id: LayerId;
  name: string;
  visible: boolean;
  opacity: number;
  fillOpacity: number;
  blendMode: BlendMode;
  locked: boolean;
  pixelsLocked: boolean;
  positionLocked: boolean;
  color: LayerColor;
  clipping: boolean;
  transform: Matrix;
  mask?: LayerMask;
  extensions: Record<string, unknown>;
}

export interface RasterLayer extends LayerBase {
  kind: 'raster';
  raster: RasterTileStore;
}

export interface GroupLayer extends LayerBase {
  kind: 'group';
  children: Layer[];
  expanded: boolean;
}

export interface AdjustmentLayer extends LayerBase {
  kind: 'adjustment';
  adjustment: {
    brightness: number;
    contrast: number;
    saturation: number;
    invert: boolean;
    levels?: LevelsSettings;
    curves?: ToneCurves;
    gradientMap?: GradientMapSettings;
    blackWhite?: BlackWhiteSettings;
  };
}

export interface VectorLayer extends LayerBase {
  kind: 'vector';
  vector: VectorScene;
}

export interface TextLayer extends LayerBase {
  kind: 'text';
  text: EditableText;
}

/** Encoded public image reference; source bytes live in retained document resources. */
export interface ImageLayer extends LayerBase {
  kind: 'image';
  image: { asset: string; frame: { width: number; height: number; fit: 'fill' | 'contain' | 'cover' } };
}

export type Layer = ImageLayer | RasterLayer | GroupLayer | AdjustmentLayer | VectorLayer | TextLayer;

export interface ImportNotice {
  severity: 'info' | 'warning' | 'error';
  feature: string;
  message: string;
  layerName?: string;
  handling: 'preserved' | 'approximated' | 'rasterized' | 'opaque-metadata' | 'omitted';
}

export interface ImportReport {
  codec: string;
  sourceName: string;
  notices: ImportNotice[];
}

export interface EditorDocument {
  schemaVersion: 1;
  id: DocumentId;
  name: string;
  width: number;
  height: number;
  resolution: { x: number; y: number; unit: 'ppi' };
  color: { space: 'srgb'; depth: 8; profileName?: string };
  pixelAspectRatio: number;
  root: GroupLayer;
  selection: SelectionState;
  activeLayerIds: LayerId[];
  createdAt: string;
  updatedAt: string;
  source?: { kind: 'new' | 'image' | 'psd' | 'maipo' | 'recovery'; name?: string };
  importReport?: ImportReport;
  guides?: Guide[];
  extensions: Record<string, unknown>;
}

export interface DocumentLimits {
  maxDimension: number;
  maxLayers: number;
  maxDecodedBytes: number;
  warningDecodedBytes: number;
}

export const DEFAULT_LIMITS: DocumentLimits = {
  maxDimension: 32_768,
  maxLayers: 4_096,
  maxDecodedBytes: 1_500 * 1024 * 1024,
  warningDecodedBytes: 512 * 1024 * 1024,
};

export function createId(prefix: string): string {
  const value = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${value}`;
}

export function createLayerBase(name: string): LayerBase {
  return {
    id: createId('layer'),
    name,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: 'normal',
    locked: false,
    pixelsLocked: false,
    positionLocked: false,
    color: 'none',
    clipping: false,
    transform: IDENTITY_MATRIX,
    extensions: {},
  };
}

export function createRasterLayer(name: string, width: number, height: number): RasterLayer {
  return { ...createLayerBase(name), kind: 'raster', raster: new RasterTileStore(width, height) };
}

/**
 * A resolution-independent, recolorable background: a canvas-sized vector
 * rectangle instead of painted pixels. Recolor the whole background with one
 * fill change; export stays crisp at any size. Built inline (type-only import
 * from vector/types) to avoid a runtime cycle with vector/model.
 */
export function createSolidBackgroundLayer(color: string, width: number, height: number): VectorLayer {
  const scene: VectorScene = {
    schemaVersion: 2,
    objects: [{
      id: createId('vector'),
      name: 'Background fill',
      visible: true,
      opacity: 1,
      transform: IDENTITY_MATRIX,
      fill: { type: 'solid', color, alpha: 1 },
      stroke: null,
      extensions: {},
      type: 'rectangle',
      geometry: { x: 0, y: 0, width, height, radius: { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 } },
    }],
    extensions: {},
  };
  return { ...createLayerBase('Background'), kind: 'vector', vector: scene };
}

export function createGroupLayer(name: string, children: Layer[] = []): GroupLayer {
  return { ...createLayerBase(name), kind: 'group', children, expanded: true };
}

export function createAdjustmentLayer(name = 'Tone adjustment'): AdjustmentLayer {
  return {
    ...createLayerBase(name),
    kind: 'adjustment',
    adjustment: {
      brightness: 0,
      contrast: 0,
      saturation: 0,
      invert: false,
      levels: { inputBlack: 0, inputWhite: 1, gamma: 1, outputBlack: 0, outputWhite: 1 },
      curves: { rgb: [], red: [], green: [], blue: [] },
      gradientMap: {
        enabled: false,
        reverse: false,
        stops: [
          { position: 0, color: '#000000' },
          { position: 0.5, color: '#808080' },
          { position: 1, color: '#ffffff' },
        ],
      },
      blackWhite: { ...DEFAULT_BLACK_WHITE },
    },
  };
}

export type DocumentBackground = 'transparent' | 'white' | 'black' | { type: 'solid'; color: string };

export function createDocument(options: {
  name: string;
  width: number;
  height: number;
  background: DocumentBackground;
}): EditorDocument {
  validateDimensions(options.width, options.height);
  let layer: Layer;
  if (typeof options.background === 'object') {
    // Vector background: a recolorable, resolution-independent solid fill.
    layer = createSolidBackgroundLayer(options.background.color, options.width, options.height);
  } else {
    const raster = createRasterLayer('Layer 1', options.width, options.height);
    if (options.background !== 'transparent') {
      const value = options.background === 'white' ? 255 : 0;
      const columns = Math.ceil(options.width / raster.raster.tileSize);
      const rows = Math.ceil(options.height / raster.raster.tileSize);
      for (let y = 0; y < rows; y += 1) {
        for (let x = 0; x < columns; x += 1) {
          const dimensions = raster.raster.tileDimensions(x, y);
          const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
          for (let index = 0; index < pixels.length; index += 4) {
            pixels[index] = value;
            pixels[index + 1] = value;
            pixels[index + 2] = value;
            pixels[index + 3] = 255;
          }
          raster.raster.setTile(x, y, pixels);
        }
      }
      raster.name = 'Background';
    }
    layer = raster;
  }
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: createId('doc'),
    name: options.name,
    width: options.width,
    height: options.height,
    resolution: { x: 72, y: 72, unit: 'ppi' },
    color: { space: 'srgb', depth: 8 },
    pixelAspectRatio: 1,
    root: createGroupLayer('Root', [layer]),
    selection: EMPTY_SELECTION,
    activeLayerIds: [layer.id],
    createdAt: now,
    updatedAt: now,
    source: { kind: 'new' },
    extensions: {},
  };
}

/** The deepest group nesting a saved project may have (root children are depth 1). */
export const MAX_LAYER_NESTING = 64;

/** Deepest nesting level of any layer below the root. Iterative: a hostile tree cannot overflow the stack. */
export function layerNestingDepth(document: Pick<EditorDocument, 'root'>): number {
  let deepest = 0;
  const stack: Array<{ layer: Layer; depth: number }> = [{ layer: document.root, depth: 0 }];
  while (stack.length > 0) {
    const { layer, depth } = stack.pop() as { layer: Layer; depth: number };
    if (depth > deepest) deepest = depth;
    if (layer.kind === 'group') for (const child of layer.children) stack.push({ layer: child, depth: depth + 1 });
  }
  return deepest;
}

export function validateDimensions(width: number, height: number, limits = DEFAULT_LIMITS): void {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new Error('Canvas dimensions must be positive integers.');
  }
  if (width > limits.maxDimension || height > limits.maxDimension) {
    throw new Error(`Canvas exceeds the ${limits.maxDimension.toLocaleString()} px dimension limit.`);
  }
  const bytes = width * height * 4;
  if (!Number.isSafeInteger(bytes) || bytes > limits.maxDecodedBytes) {
    throw new Error('Canvas exceeds the decoded memory safety limit.');
  }
}

export function findLayer(root: GroupLayer, id: LayerId): Layer | undefined {
  for (const layer of root.children) {
    if (layer.id === id) return layer;
    if (layer.kind === 'group') {
      const nested = findLayer(layer, id);
      if (nested) return nested;
    }
  }
  return undefined;
}

export function findLayerParent(root: GroupLayer, id: LayerId): { parent: GroupLayer; index: number } | undefined {
  const index = root.children.findIndex((layer) => layer.id === id);
  if (index >= 0) return { parent: root, index };
  for (const layer of root.children) {
    if (layer.kind === 'group') {
      const nested = findLayerParent(layer, id);
      if (nested) return nested;
    }
  }
  return undefined;
}

export function walkLayers(root: GroupLayer): Layer[] {
  const result: Layer[] = [];
  for (const layer of root.children) {
    result.push(layer);
    if (layer.kind === 'group') result.push(...walkLayers(layer));
  }
  return result;
}

function utf16Bytes(value: unknown): number {
  try {
    return JSON.stringify(value).length * 2;
  } catch {
    return 0;
  }
}

/** Retained heap owned by a layer, including complete nested stores and extension payloads. */
export function layerRetainedBytes(layer: Layer): number {
  const bytes = 512 + utf16Bytes(layer.extensions) + (layer.mask?.tiles.byteLength() ?? 0);
  if (layer.kind === 'raster') return bytes + layer.raster.byteLength();
  if (layer.kind === 'group') return bytes + layer.children.reduce((sum, child) => sum + layerRetainedBytes(child), 0);
  if (layer.kind === 'vector') return bytes + utf16Bytes(layer.vector);
  if (layer.kind === 'text') return bytes + utf16Bytes(layer.text);
  if (layer.kind === 'image') return bytes + utf16Bytes(layer.image);
  return bytes + utf16Bytes(layer.adjustment);
}

function pointsBounds(points: readonly Point[]): Rect | null {
  if (points.length === 0) return null;
  // Scanned rather than spread into Math.min. Argument spreads throw
  // RangeError past roughly 125k arguments, and a path carries one point per
  // anchor plus one per Bezier handle — so a traced region large enough to be
  // worth curve-fitting is within reach of crashing its own bounds check.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function unionBounds(bounds: readonly Rect[]): Rect | null {
  if (bounds.length === 0) return null;
  // Same reason as pointsBounds: a traced layer carries hundreds of objects and
  // this is fed one rect per object.
  let x = Infinity;
  let y = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const rect of bounds) {
    if (rect.x < x) x = rect.x;
    if (rect.y < y) y = rect.y;
    if (rect.x + rect.width > right) right = rect.x + rect.width;
    if (rect.y + rect.height > bottom) bottom = rect.y + rect.height;
  }
  return { x, y, width: right - x, height: bottom - y };
}

export function vectorObjectLocalBounds(object: VectorScene['objects'][number]): Rect | null {
  let bounds: Rect | null;
  if (object.type === 'rectangle' || object.type === 'ellipse') {
    bounds = object.geometry;
  } else {
    const points = object.geometry.nodes.flatMap((node): Point[] => [node.anchor, ...(node.incoming ? [node.incoming] : []), ...(node.outgoing ? [node.outgoing] : [])]);
    bounds = pointsBounds(points);
  }
  if (!bounds) return null;
  const strokePadding = (object.stroke?.width ?? 0) / 2;
  return {
    x: bounds.x - strokePadding,
    y: bounds.y - strokePadding,
    width: bounds.width + strokePadding * 2,
    height: bounds.height + strokePadding * 2,
  };
}

/** Local editable bounds. Layer transforms are intentionally not included. */
export function layerBounds(layer: Layer): Rect | null {
  if (layer.kind === 'raster') return { x: 0, y: 0, width: layer.raster.width, height: layer.raster.height };
  if (layer.kind === 'text') return textLayoutBounds(layer.text);
  if (layer.kind === 'image') return { x: 0, y: 0, width: layer.image.frame.width, height: layer.image.frame.height };
  if (layer.kind === 'vector') return unionBounds(layer.vector.objects.filter((object) => object.visible).flatMap((object) => {
    const bounds = vectorObjectLocalBounds(object);
    return bounds ? [transformRectBoundsPrecise(object.transform, bounds)] : [];
  }));
  if (layer.kind === 'group') return unionBounds(layer.children.filter((child) => child.visible).flatMap((child) => {
    const bounds = layerBounds(child);
    return bounds ? [transformRectBoundsPrecise(child.transform, bounds)] : [];
  }));
  return null;
}

export function layerParentTransform(root: GroupLayer, id: LayerId, parentTransform: Matrix = IDENTITY_MATRIX): Matrix | null {
  const childParent = multiplyMatrix(parentTransform, root.transform);
  for (const layer of root.children) {
    if (layer.id === id) return childParent;
    if (layer.kind === 'group') {
      const nested = layerParentTransform(layer, id, childParent);
      if (nested) return nested;
    }
  }
  return null;
}

export function layerDocumentTransform(root: GroupLayer, id: LayerId): Matrix | null {
  const layer = findLayer(root, id);
  const parent = layerParentTransform(root, id);
  return layer && parent ? multiplyMatrix(parent, layer.transform) : null;
}

function pointInside(rect: Rect, point: Point): boolean {
  return point.x >= rect.x && point.y >= rect.y && point.x < rect.x + rect.width && point.y < rect.y + rect.height;
}

function maskAlphaAt(mask: LayerMask, point: Point): number {
  const x = Math.floor(point.x);
  const y = Math.floor(point.y);
  if (x < 0 || y < 0 || x >= mask.tiles.width || y >= mask.tiles.height) return 0;
  const tileX = Math.floor(x / mask.tiles.tileSize);
  const tileY = Math.floor(y / mask.tiles.tileSize);
  const dimensions = mask.tiles.tileDimensions(tileX, tileY);
  const localX = x - tileX * mask.tiles.tileSize;
  const localY = y - tileY * mask.tiles.tileSize;
  const offset = (localY * dimensions.width + localX) * 4 + 3;
  return mask.tiles.getTile(tileX, tileY)?.[offset] ?? mask.defaultAlpha;
}

function rasterAlphaAt(layer: RasterLayer, point: Point): number {
  const x = Math.floor(point.x);
  const y = Math.floor(point.y);
  if (x < 0 || y < 0 || x >= layer.raster.width || y >= layer.raster.height) return 0;
  const tileX = Math.floor(x / layer.raster.tileSize);
  const tileY = Math.floor(y / layer.raster.tileSize);
  const dimensions = layer.raster.tileDimensions(tileX, tileY);
  const localX = x - tileX * layer.raster.tileSize;
  const localY = y - tileY * layer.raster.tileSize;
  const offset = (localY * dimensions.width + localX) * 4 + 3;
  const pixelAlpha = layer.raster.getTile(tileX, tileY)?.[offset] ?? 0;
  if (!layer.mask?.enabled || pixelAlpha === 0) return pixelAlpha;
  return pixelAlpha * maskAlphaAt(layer.mask, point) / 255;
}

/** Returns editable layer candidates in visual front-to-back order. */
export function findLayersAtPoint(root: GroupLayer, point: Point, alphaThreshold = 8): Layer[] {
  return findLayersAtPointWithTransform(root, point, alphaThreshold, IDENTITY_MATRIX);
}

function findLayersAtPointWithTransform(root: GroupLayer, point: Point, alphaThreshold: number, parentTransform: Matrix): Layer[] {
  const matches: Layer[] = [];
  const childParent = multiplyMatrix(parentTransform, root.transform);
  const rootInverse = root.mask?.enabled ? invertMatrix(childParent) : null;
  if (root.mask?.enabled && !rootInverse) return matches;
  const rootLocalPoint = rootInverse ? transformPoint(rootInverse, point) : null;
  if (root.mask?.enabled && rootLocalPoint && maskAlphaAt(root.mask, rootLocalPoint) < alphaThreshold) return matches;
  for (const layer of root.children) {
    if (!layer.visible || layer.opacity <= 0 || layer.fillOpacity <= 0) continue;
    if (layer.kind === 'group') {
      matches.push(...findLayersAtPointWithTransform(layer, point, alphaThreshold, childParent));
      continue;
    }
    if (layer.kind === 'adjustment') continue;
    const inverse = invertMatrix(multiplyMatrix(childParent, layer.transform));
    const bounds = layerBounds(layer);
    if (!inverse || !bounds) continue;
    const localPoint = transformPoint(inverse, point);
    if (!pointInside(bounds, localPoint)) continue;
    if (layer.kind === 'raster') {
      if (rasterAlphaAt(layer, localPoint) >= alphaThreshold) matches.push(layer);
      continue;
    }
    if (!layer.mask?.enabled || maskAlphaAt(layer.mask, localPoint) >= alphaThreshold) matches.push(layer);
  }
  return matches;
}

/** Returns the visually topmost editable layer at a document-space point. */
export function findTopLayerAtPoint(root: GroupLayer, point: Point, alphaThreshold = 8): Layer | undefined {
  return findLayersAtPoint(root, point, alphaThreshold)[0];
}

export function estimateDocumentBytes(document: EditorDocument): number {
  return 1024 + utf16Bytes(document.extensions) + utf16Bytes(document.selection) + layerRetainedBytes(document.root);
}
